/**
 * V49 — Orientation picker used in:
 *   • Settings → Display Settings → Screen Orientation
 *   • Profile → quick toggle
 *
 * Pure UI; persistence is delegated to the useAdminOrientation hook.
 */
import { Smartphone, Monitor, RotateCcw } from "lucide-react";
import {
    useAdminOrientation,
    ORIENTATION_LABELS,
    ORIENTATION_DESCRIPTIONS,
} from "../../lib/adminOrientation";

const ICONS = {
    portrait: Smartphone,
    landscape: Monitor,
    auto: RotateCcw,
};

const RECOMMENDED = "portrait";

export default function OrientationPicker({ compact = false }) {
    const { mode, setMode } = useAdminOrientation();

    return (
        <div
            className={`grid gap-3 ${compact ? "grid-cols-3" : "sm:grid-cols-3"}`}
            data-testid="orientation-picker"
        >
            {Object.entries(ORIENTATION_LABELS).map(([key, label]) => {
                const Icon = ICONS[key];
                const isActive = mode === key;
                const isRecommended = key === RECOMMENDED;
                return (
                    <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setMode(key)}
                        data-testid={`orientation-option-${key}`}
                        className={`relative text-left rounded-2xl border-2 px-3 py-3 transition-colors ${
                            isActive
                                ? "border-[#1E3A8A] bg-blue-50 ring-2 ring-[#1E3A8A]/15"
                                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            <span
                                className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl ${
                                    isActive ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-semibold text-[#0F172A]">{label}</span>
                                    {isRecommended && (
                                        <span className="text-[10px] font-bold tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                                            RECOMMENDED
                                        </span>
                                    )}
                                </div>
                                {!compact && (
                                    <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
                                        {ORIENTATION_DESCRIPTIONS[key]}
                                    </p>
                                )}
                            </div>
                        </div>
                        {isActive && (
                            <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                        )}
                    </button>
                );
            })}
        </div>
    );
}
